// Improvement row L13 — "time to first results" on a landing that searches by itself.
//
// On the homepage, URL-parameter landings (?city=, ?startdate=, ?lat=…, ?carid=) run a
// search before any input, and the first results are the largest paint on phones (RUM:
// 36 % of mobile homepage LCP samples, the on-sale card at p75 5.3 s in that cohort).
// Nothing between navigation and that paint is measured. This module keeps the marks of
// ONE startup attempt per page load — boot, mount, geocoding, parameters, the request,
// the paint opportunity after the results commit — and reports them once as a single
// GA4 event `rrm_startup` (22 parameters + the wrapper's two; GA4 allows 25). Read in
// BigQuery; no GA4 registration needed.
//
// Rules: every hook is a synchronous `performance.now()` read; nothing here delays or
// changes the search, and a throwing reporter is contained here. A mark that never
// happened is sent as -1, never omitted, so a broken hook is visible in the data. A
// landing that does not search on its own (plain homepage visit, `startup_kind` none)
// is not armed and reports nothing.
import type {StartupKind, StartupKindInput, StartupOutcome, StartupReporter, StartupState} from '../interfaces/StartupTiming';

export type {StartupKind, StartupKindInput, StartupOutcome, StartupReporter} from '../interfaces/StartupTiming';

// Reported with -1 when the startup never reached that stage.
const MISSING = -1;
// Telemetry deadline: an attempt still pending this long after arming is reported as
// censored (with the marks it has), so the slow tail is in the data instead of lost.
export const STARTUP_DEADLINE_MS = 20_000;

const now = () => Math.round(performance.now());

function fresh(): StartupState {
    return {
        armed: false, reported: false, kind: 'none', geocoded: false, attempt: 0, paramsChanges: 0,
        apiReady: MISSING, resultsCount: MISSING, firstInputAt: MISSING, wasHidden: false,
        t: {boot: MISSING, mount: MISSING, geoLib: MISSING, geoDone: MISSING, paramsFirst: MISSING,
            params: MISSING, requestStart: MISSING, requestEnd: MISSING, visible: MISSING},
        cleanup: [],
    };
}

let state = fresh();

// Which landings search on their own. `keep` from resolveUrlLocation covers both a
// session-restored search (a2a needs a location, one-way needs both cities) and no
// location at all; only the former is a startup search.
export function startupKindFor(input: StartupKindInput): StartupKind {
    // A one-way search runs on the session's pickup/return cities only: the location a
    // ?city=, code or acriss link resolves to is not searched, nor is a vehicle link's
    // Helsinki (App.tsx startupLocation). So in one-way only saved cities count.
    if (input.locationSource !== 'keep' && !input.oneWay) return input.locationSource;
    if (input.vehicleLink && !input.oneWay) return 'vehicle-link';
    const restored = input.oneWay ? (input.savedPickupCity && input.savedReturnCity) : input.savedLocation;
    return restored ? 'saved' : 'none';
}

// Entry module evaluated — the earliest moment the widget's own code runs.
export function markBoot() { if (state.t.boot === MISSING) state.t.boot = now(); }

// App committed for the first time.
export function markMount() { if (state.t.mount === MISSING) state.t.mount = now(); }

// Called once from App's startup effect with the resolved landing kind. The reporter
// should delegate to the latest sendGa4Event, not a render's closure, so the wrapper's
// defaults (surface, vehicle_segment) reflect the parameters the startup search used.
export function armStartup(kind: StartupKind, reporter: StartupReporter, opts: {geocoded?: boolean} = {}) {
    if (state.armed || state.reported) return;
    state.kind = kind;
    if (kind === 'none') return;
    state.armed = true;
    state.geocoded = !!opts.geocoded;
    state.reporter = reporter;
    state.wasHidden = document.visibilityState === 'hidden';

    const onInput = () => { if (state.firstInputAt === MISSING) state.firstInputAt = now(); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') state.wasHidden = true; };
    // The page is going away (navigation, tab close, or into the bfcache): whatever is
    // still pending is reported as abandoned now, never completed after a restore.
    const onPageHide = () => report('abandoned');
    for (const type of ['keydown', 'pointerdown'] as const) {
        window.addEventListener(type, onInput, {capture: true, passive: true});
        state.cleanup.push(() => window.removeEventListener(type, onInput, {capture: true}));
    }
    document.addEventListener('visibilitychange', onVisibility);
    state.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));
    window.addEventListener('pagehide', onPageHide);
    state.cleanup.push(() => window.removeEventListener('pagehide', onPageHide));
    state.deadline = setTimeout(() => report('censored'), STARTUP_DEADLINE_MS);
}

// The `?city=` path: Google's geocoding library arrived, then the geocode answered.
export function markGeocodeLibrary() { if (state.armed && state.t.geoLib === MISSING) state.t.geoLib = now(); }
export function markGeocodeDone() { if (state.armed && state.t.geoDone === MISSING) state.t.geoDone = now(); }

// The search effect saw usable parameters. It can run several times while startup
// settles (the debounce restarts each time); `params` is the LAST scheduling before the
// request that ran, `paramsFirst` the first, and the count says how much churn there was.
export function markParamsReady(apiReady: boolean) {
    if (!state.armed || state.attempt !== 0) return;
    const at = now();
    if (state.t.paramsFirst === MISSING) { state.t.paramsFirst = at; state.apiReady = apiReady ? 1 : 0; }
    state.t.params = at;
    state.paramsChanges += 1;
}

// A search request is starting. The first one after arming becomes the startup
// attempt; a later one while the attempt is still pending means the startup was
// superseded (parameters changed under it) and is reported as such.
export function markRequestStart(fetchId: number) {
    if (!state.armed) return;
    if (state.attempt === 0) {
        state.attempt = fetchId;
        state.t.requestStart = now();
        return;
    }
    if (!state.reported && fetchId !== state.attempt) report('superseded');
}

// `resultsCount` is what the API reported (or the list length when it reported none);
// `vehiclesReceived` decides emptiness — nothing will paint for an empty list, so that
// is the terminal outcome.
export function markRequestEnd(fetchId: number, resultsCount: number, vehiclesReceived: number) {
    if (!state.armed || fetchId !== state.attempt || state.t.requestEnd !== MISSING) return;
    state.t.requestEnd = now();
    state.resultsCount = resultsCount;
    if (vehiclesReceived === 0) report('empty');
}

export function markRequestFailed(fetchId: number) {
    if (!state.armed || fetchId !== state.attempt) return;
    if (state.t.requestEnd === MISSING) state.t.requestEnd = now();
    report('error');
}

// The results of `fetchId` are committed and about to be painted. Two animation frames
// later is the paint opportunity (an approximation, not the LCP entry). `stillShown`
// is re-read in the callback: if the content was cleared, replaced or unmounted in
// between, nothing painted and the attempt stays pending for its real outcome.
export function markResultsVisible(fetchId: number, stillShown: () => boolean) {
    if (!state.armed || state.reported || fetchId !== state.attempt || state.t.visible !== MISSING || state.frame !== undefined) return;
    state.frame = requestAnimationFrame(() => {
        state.frame = requestAnimationFrame(() => {
            state.frame = undefined;
            if (state.reported || fetchId !== state.attempt || !stillShown()) return;
            state.t.visible = now();
            report('visible');
        });
    });
}

function navigationType(): string {
    // activationStart is the prerender API (Chrome); not in the DOM lib typings yet.
    const entry = performance.getEntriesByType?.('navigation')[0] as (PerformanceNavigationTiming & {activationStart?: number}) | undefined;
    if (!entry) return 'unknown';
    if (entry.activationStart && entry.activationStart > 0) return 'prerender';
    return entry.type || 'unknown';
}

const diff = (a: number, b: number) => (a === MISSING || b === MISSING ? MISSING : a - b);

function report(outcome: StartupOutcome) {
    if (!state.armed || state.reported) return;
    state.reported = true;
    if (state.deadline !== undefined) clearTimeout(state.deadline);
    if (state.frame !== undefined) { cancelAnimationFrame(state.frame); state.frame = undefined; }
    for (const off of state.cleanup) off();
    state.cleanup = [];
    const t = state.t;
    const inputBefore = state.firstInputAt !== MISSING && (t.requestStart === MISSING || state.firstInputAt < t.requestStart) ? 1 : 0;
    const inputDuring = state.firstInputAt !== MISSING && !inputBefore && (t.visible === MISSING || state.firstInputAt < t.visible) ? 1 : 0;
    try {
        state.reporter?.('rrm_startup', {
            startup_kind: state.kind,
            outcome,
            nav_type: navigationType(),
            geocoded: state.geocoded ? 1 : 0,
            api_ready: state.apiReady,
            params_changes: state.paramsChanges,
            input_before: inputBefore,
            input_during: inputDuring,
            was_hidden: state.wasHidden ? 1 : 0,
            results_count: state.resultsCount,
            t_boot: t.boot,
            t_mount: t.mount,
            t_geo_lib: t.geoLib,
            t_geo_done: t.geoDone,
            t_params_first: t.paramsFirst,
            t_params: t.params,
            t_request_start: t.requestStart,
            t_request_end: t.requestEnd,
            t_visible: t.visible,
            d_debounce: diff(t.requestStart, t.params),
            d_request: diff(t.requestEnd, t.requestStart),
            d_render: diff(t.visible, t.requestEnd),
        });
    } catch {
        // Telemetry must never break the search that called it.
    }
}

// Test seam: the module is a per-page-load singleton.
export function resetStartupTimingForTests() {
    if (state.deadline !== undefined) clearTimeout(state.deadline);
    if (state.frame !== undefined) cancelAnimationFrame(state.frame);
    for (const off of state.cleanup) off();
    state = fresh();
}
export function startupTimingSnapshot() {
    return {armed: state.armed, reported: state.reported, kind: state.kind, geocoded: state.geocoded, attempt: state.attempt, marks: {...state.t}};
}
